import { getAxiosBackend, getAuthAxiosBackend } from '../../helpers/axios';
import User from '../../helpers/user';
import { defineStore } from "pinia";
import { jwtDecode } from "jwt-decode";
import { PersonTypeCategory, PersonTypeCategoryId } from "@/constants/personTypeAccess";

export const useAuthStore = defineStore("auth", {

state:()=>({
    toLocaleString: false,
    token: getState('token'),
    user: getState('user'),
}),
getters : {
    // Whether the user is currently logged in.
    loggedIn() {
        return !!this.token
    },
    getToken(){
        return this.token
    },
    getUser(){
        return this.token != null && this.user != null ? 
        new User(
            this.user.userId ?? this.user.id,
            this.user.userType ?? this.user.user_type,
            this.user.active,
            this.user.personName ?? this.user.name,
            this.user.username,
            this.user.roleNames ?? this.user.roleName,
            this.user.permissionNames,
            this.user.categoryNames ?? this.user.categories,
            this.user.personTypeNames ?? this.user.person_types ?? this.user.personTypes,
            this.user.lastLogin ?? this.user.last_login,
            this.user.personTypeOperations,
            this.user.categoryPersonTypeOperations,
            this.user.governorateIds,
            this.user.districtIds,
            this.user.townshipIds,
            this.user.regionIds
        ) : new User()
    }
},
actions : {
    /** Sets */
    setToken(token) {
        if(token != null){
            this.token = token
            saveState('token', token, this.toLocaleString)
        }
    },
    setUser(user) {
        if(user != null){
            this.user = this.getUser

            this.user.id            = user.userId
            this.user.active        = user.active
            this.user.user_type     = user.userType
            this.user.userType      = user.userType
            this.user.name          = user.personName
            this.user.personName    = user.personName
            this.user.username      = user.username
            this.user.last_login    = user.lastLogin
            this.user.lastLogin     = user.lastLogin

            // Fetch token from local storage.
            const decoded = jwtDecode(this.token)
            // console.log(decoded)
            this.user.roleNames         = decoded['roles']//.flatMap(r=>r.permissions.map(p=>p)) ?? []
            this.user.permissionNames   = decoded['permissions'] ?? []

            const categories         = mapPersonTypesCategoryToIds(decoded['categories'])
            this.user.categories        = categories
            this.user.categoryNames     = categories

            const personTypeIds         = mapPersonTypesToIds(decoded['personTypes'])
            this.user.person_types      = personTypeIds
            this.user.personTypeNames   = personTypeIds

            this.user.personTypeOperations = decoded['personTypeOperations'] ?? {}
            this.user.categoryPersonTypeOperations = decoded['categoryPersonTypeOperations'] ?? {},
            this.user.regionIds         = decoded['regionIds'] ?? []
        }
        saveState('user', this.user, this.toLocaleString)
    },
    setUsername(username){
        this.user.username = username
        saveState('user', this.user, this.toLocaleString)
    },
    setAdditionalData(data){
        if(data.regions) this.user.setTownshipIds(data.regions)
        if(data.townships) this.user.setDistrictIds(data.townships)
        if(data.districts) this.user.setGovernorateIds(data.districts)

        saveState('user', this.user, this.toLocaleString)
    },
    /** Sets */

    logIn({ username, password , remember} = {}) {
        this.toLocaleString = remember
        
        if (this.loggedIn) return this.validate()
        // console.log(getAxiosBackend())
        return getAxiosBackend().post('Security/login', {
            username:username,
            password:password,
        })
        .then((response)=>{
            // console.log('login response')
            // console.log(response)
            const token = response.data.data.token
            this.setToken(token)
            return this.my()
        });
    },
    
    validate() {
        if (!this.token) return Promise.resolve(null)

        if(process.env.VUE_APP_API_VALIDATE_TOKEN == 'serverSide')
        {
            return this.my()
            .catch((error)=>{
                this.logOutClientSide()
                return error
            })
        }else{ //client Side
            Promise.resolve(200)
        }
    },

    my(){
        return getAuthAxiosBackend().get('Security/user-info',{params: {
            roles_permissions: true,
            permissions: true
        }})
            .then((response)=>{
            // console.log('my response')
            // console.log(response)

                this.setUser(response.data.data)
                return 200;
            })
    },

    logOut() {
        return getAuthAxiosBackend().post('Security/logout')
        .then((response)=>{
            this.logOutClientSide()
            return response
        })
    },

    completeLogOut() {
        return this.logOut()
        .catch(() => {
            // Ensure local session is cleared even if backend logout fails.
            this.logOutClientSide();
            return 200;
        });
    },

    logOutClientSide(){
        this.token = null
        this.user = null
        delete getAuthAxiosBackend().defaults.headers.common['Authorization']
        clearState();
    },

    changePassword({old_password, new_password, new_password_confirmation}){
        return getAuthAxiosBackend().put('Security/change-password', {
            old_password: old_password,
            new_password: new_password,
            new_password_confirmation: new_password_confirmation,
        })
    },

    changeUsername({username}){
        return getAuthAxiosBackend().put('Security/change-username', {
            newUsername: username
        }).then((response)=>{
            this.setUsername(username)
            return response
        })
    },

    checkUserName(data){
        return getAuthAxiosBackend().post('Security/check-username', data);
    }
}
});
// ===
// Private helpers
// ===

function saveState(key, state, toLocaleString) {
    if(toLocaleString) window.localStorage.setItem(key, JSON.stringify(state))
    else window.sessionStorage.setItem(key, JSON.stringify(state));
}
function getState(key){
    return JSON.parse(
        window.sessionStorage.getItem(key) ?? window.localStorage.getItem(key)
    )
}

function mapPersonTypesToIds(personTypes = []){
    if(!Array.isArray(personTypes)) return []

    const mapByArabicName = {
        'شهید': 1,
        'جريح': 2,
        'متوفي': 3,
        'عائلة الشهيد': 4,
        'عائلة الجريح': 5,
        'عائلة المتوفي': 6,
    }

    return [...new Set(personTypes
        .map((item) => {
            if(item == null) return null

            if(typeof item === 'number') return item

            const normalized = String(item).trim()
            if(normalized === '') return null

            if(/^\d+$/.test(normalized)) return Number(normalized)
            return mapByArabicName[normalized] ?? null
        })
        .filter((id) => id != null))]
}

function mapPersonTypesCategoryToIds(categoriesName = []){
    if(!Array.isArray(categoriesName)) return []

    const mapByEnglishName = {
        [PersonTypeCategory.ADMINISTRATIVE]: PersonTypeCategoryId.ADMINISTRATIVE,
        [PersonTypeCategory.ATTACHMENT]: PersonTypeCategoryId.ATTACHMENT,
        [PersonTypeCategory.SACRIFICE]: PersonTypeCategoryId.SACRIFICE,
        [PersonTypeCategory.RESIDENCE]: PersonTypeCategoryId.RESIDENCE,
        [PersonTypeCategory.EDUCATION]: PersonTypeCategoryId.EDUCATION,
        [PersonTypeCategory.JOB_AND_EMPLOYMENT]: PersonTypeCategoryId.JOB_AND_EMPLOYMENT,
        [PersonTypeCategory.CONTACTS]: PersonTypeCategoryId.CONTACTS,
        [PersonTypeCategory.CIVILITY]: PersonTypeCategoryId.CIVILITY,
    }

    return [...new Set(categoriesName
        .map((item) => {
            if(item == null) return null

            if(typeof item === 'number') return item

            const normalized = String(item).trim()
            if(normalized === '') return null

            return mapByEnglishName[normalized] ?? null
        })
        .filter((id) => id != null))]
        .sort((a, b) => a - b)
}

function clearState(){
    window.localStorage.clear()
    window.sessionStorage.clear()
}
